import { supabase } from '@/lib/supabase';
import { createLogger } from '@/lib/logger';
import { cache, CacheKeys, CacheTTL } from '@/lib/cache';
import type { Product, CreateProductInput, UpdateProductInput } from '@/types/product.types';

const log = createLogger('ProductService');

interface ServiceResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * Service pour la gestion des produits
 * Version simplifiée sans mode offline pour éviter les problèmes de synchronisation
 */
class ProductService {
  /**
   * Récupérer tous les produits du restaurant (avec cache)
   */
  async getProducts(restaurantId: string, forceRefresh = false): Promise<ServiceResponse<Product[]>> {
    const cacheKey = CacheKeys.products(restaurantId);

    // Vérifier le cache sauf si refresh forcé
    if (!forceRefresh) {
      const cached = cache.get<Product[]>(cacheKey);
      if (cached) {
        return { success: true, data: cached };
      }
    }

    try {
      const { data, error } = await supabase
        .from('produits')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .order('nom', { ascending: true });

      if (error) throw error;

      const products = data as Product[];

      // Mettre en cache
      cache.set(cacheKey, products, { ttl: CacheTTL.REFERENCE });

      return {
        success: true,
        data: products,
      };
    } catch (error) {
      log.error('Error fetching products', { error });

      // En cas d'erreur, retourner les données stale si disponibles
      const stale = cache.getStale<Product[]>(cacheKey);
      if (stale) {
        return { success: true, data: stale };
      }

      return {
        success: false,
        error: this.formatError(error),
      };
    }
  }

  /**
   * Récupérer un produit par son ID
   */
  async getProductById(id: string): Promise<ServiceResponse<Product>> {
    try {
      const { data, error } = await supabase
        .from('produits')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      return {
        success: true,
        data: data as Product,
      };
    } catch (error) {
      log.error('Error fetching product', { error });
      return {
        success: false,
        error: this.formatError(error),
      };
    }
  }

  /**
   * Créer un nouveau produit avec gestion automatique du stock
   */
  async createProduct(
    restaurantId: string,
    input: CreateProductInput
  ): Promise<ServiceResponse<Product>> {
    try {
      // Validation des valeurs numériques
      if (isNaN(input.seuil_critique) || isNaN(input.seuil_alerte)) {
        return {
          success: false,
          error: 'Les seuils critique et alerte doivent être des nombres valides',
        };
      }

      // Préparer les données d'insertion
      const insertData = {
        restaurant_id: restaurantId,
        nom: input.nom,
        categorie: input.categorie,
        unite: input.unite,
        code_barre: input.code_barre || null,
        image_url: null,
        seuil_critique: input.seuil_critique,
        seuil_alerte: input.seuil_alerte,
        cout_unitaire: input.cout_unitaire || null,
        fournisseur: input.fournisseur || null,
        taux_tva: input.taux_tva || 20,
        duree_conservation_jours: input.duree_conservation_jours || null,
      };

      // 1. Créer le produit
      const { data: product, error: productError } = await supabase
        .from('produits')
        .insert(insertData)
        .select()
        .single();

      if (productError) {
        throw productError;
      }

      const createdProduct = product as Product;

      // 2. Créer le stock initial uniquement si spécifié
      if (input.stock_initial && input.stock_initial.quantite > 0) {
        const { error: stockError } = await supabase.from('stocks').insert({
          restaurant_id: restaurantId,
          produit_id: createdProduct.id,
          zone: input.stock_initial.zone,
          quantite: input.stock_initial.quantite,
        });

        if (stockError) {
          log.error('Error creating initial stock', { error: stockError });
          // On ne bloque pas la création du produit si le stock échoue
        }

        // 3. Créer un lot si une DLC est spécifiée
        if (input.stock_initial.dlc) {
          const numeroLot = input.stock_initial.numero_lot ||
            `LOT-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;

          // Note: La table 'lots' doit être ajoutée aux types Supabase (npx supabase gen types)
          const { error: lotError } = await (supabase as any).from('lots').insert({
            restaurant_id: restaurantId,
            produit_id: createdProduct.id,
            numero_lot: numeroLot,
            dlc: input.stock_initial.dlc,
            quantite_initiale: input.stock_initial.quantite,
            quantite_restante: input.stock_initial.quantite,
            zone: input.stock_initial.zone,
            date_reception: new Date().toISOString().split('T')[0],
            prix_achat: input.cout_unitaire || null,
            statut: 'actif',
          });

          if (lotError) {
            log.error('Error creating lot', { error: lotError });
            // On ne bloque pas la création du produit si le lot échoue
          }
        }
      }

      // Invalider le cache des produits
      cache.delete(CacheKeys.products(restaurantId));

      return {
        success: true,
        data: createdProduct,
      };
    } catch (error) {
      log.error('Error creating product', { error });
      return {
        success: false,
        error: this.formatError(error),
      };
    }
  }

  /**
   * Mettre à jour un produit
   */
  async updateProduct(input: UpdateProductInput): Promise<ServiceResponse<Product>> {
    try {
      const { id, ...updates } = input;

      const { data, error } = await supabase
        .from('produits')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      // Invalider tous les caches de produits (on ne connaît pas le restaurant_id)
      cache.invalidatePrefix('products:');

      return {
        success: true,
        data: data as Product,
      };
    } catch (error) {
      log.error('Error updating product', { error });
      return {
        success: false,
        error: this.formatError(error),
      };
    }
  }

  /**
   * Supprimer un produit
   */
  async deleteProduct(id: string): Promise<ServiceResponse<void>> {
    try {
      const { error } = await supabase.from('produits').delete().eq('id', id);

      if (error) throw error;

      // Invalider tous les caches de produits
      cache.invalidatePrefix('products:');

      return {
        success: true,
      };
    } catch (error) {
      log.error('Error deleting product', { error });
      return {
        success: false,
        error: this.formatError(error),
      };
    }
  }

  /**
   * Rechercher des produits par nom ou code-barre
   */
  async searchProducts(
    restaurantId: string,
    query: string
  ): Promise<ServiceResponse<Product[]>> {
    try {
      const { data, error } = await supabase
        .from('produits')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .or(`nom.ilike.%${query}%,code_barre.ilike.%${query}%`)
        .order('nom', { ascending: true });

      if (error) throw error;

      return {
        success: true,
        data: data as Product[],
      };
    } catch (error) {
      log.error('Error searching products', { error });
      return {
        success: false,
        error: this.formatError(error),
      };
    }
  }

  /**
   * Récupérer les produits par catégorie
   */
  async getProductsByCategory(
    restaurantId: string,
    categorie: string
  ): Promise<ServiceResponse<Product[]>> {
    try {
      const { data, error } = await supabase
        .from('produits')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .eq('categorie', categorie)
        .order('nom', { ascending: true});

      if (error) throw error;

      return {
        success: true,
        data: data as Product[],
      };
    } catch (error) {
      log.error('Error fetching products by category', { error });
      return {
        success: false,
        error: this.formatError(error),
      };
    }
  }

  /**
   * Formater les erreurs
   */
  private formatError(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }
    return 'Une erreur inattendue est survenue';
  }
}

export const productService = new ProductService();
